import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, ArrowLeft, X, Heart, ShareNetwork, DownloadSimple, Play, Trophy, Clock, Fire, Star, FilmStrip } from '@phosphor-icons/react';
import { io } from 'socket.io-client';
import BackButton from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LIKES_KEY = (slug) => `pocket_likes_${slug}`;
const getLiked = (slug) => JSON.parse(localStorage.getItem(LIKES_KEY(slug)) || '[]');
const saveLiked = (slug, ids) => localStorage.setItem(LIKES_KEY(slug), JSON.stringify(ids));

const MOMENT_LABELS = [
  { label: 'Arrival', icon: '\u{1F389}', color: '#B89762' },
  { label: 'Ceremony', icon: '\u{1F48D}', color: '#C29D52' },
  { label: 'Celebration', icon: '\u{1F942}', color: '#FF0099' },
  { label: 'Dance Floor', icon: '\u{1F483}', color: '#00FFDD' },
  { label: 'Late Night', icon: '\u{1F319}', color: '#FF6B6B' },
];

const COLORS = ['#B89762', '#C29D52', '#FF0099', '#00FFDD', '#FF6B6B', '#FFCC00', '#A033D8', '#26A69A'];

// Build full URL for storage-backed media
const mediaUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

// Normalize backend doc to frontend item shape
const normalizeItem = (doc) => ({
  id: doc.media_id || doc.id,
  type: doc.type || 'booth',
  template: doc.template || null,
  mode: doc.mode || 'classic',
  imageUrl: mediaUrl(doc.image_url || doc.imageUrl || ''),
  thumbnailUrl: mediaUrl(doc.thumb_url || doc.thumbnailUrl || ''),
  likes: doc.likes || 0,
  views: doc.views || 0,
  score: doc.score || 0,
  createdAt: doc.created_at || doc.createdAt || new Date().toISOString(),
  momentLabel: doc.moment_label || null,
});

// Cluster items into time-based moments (20 min windows)
const clusterIntoMoments = (items) => {
  if (!items.length) return [];
  const sorted = [...items].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const moments = [];
  let cluster = [sorted[0]];
  let clusterStart = new Date(sorted[0].createdAt);

  for (let i = 1; i < sorted.length; i++) {
    const t = new Date(sorted[i].createdAt);
    if ((t - clusterStart) > 1200000) {
      const ml = MOMENT_LABELS[moments.length % MOMENT_LABELS.length];
      moments.push({ ...ml, items: cluster, start: clusterStart });
      cluster = [sorted[i]];
      clusterStart = t;
    } else {
      cluster.push(sorted[i]);
    }
  }
  if (cluster.length) {
    const ml = MOMENT_LABELS[moments.length % MOMENT_LABELS.length];
    moments.push({ ...ml, items: cluster, start: clusterStart });
  }
  return moments;
};

const PocketGallery = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [eventName, setEventName] = useState('');
  const [view, setView] = useState('feed');
  const [filter, setFilter] = useState('all');
  const [newItemFlash, setNewItemFlash] = useState(null);
  const [likedIds, setLikedIds] = useState([]);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  // Connect to Socket.IO and fetch initial gallery data
  useEffect(() => {
    setLikedIds(getLiked(slug));

    // Fetch initial gallery items from API
    fetch(`${API}/pocket-booth/${slug}/gallery`)
      .then(r => r.json())
      .then(data => {
        const normalized = (data.items || []).map(normalizeItem);
        setItems(normalized);
      })
      .catch(err => console.error('Gallery fetch error:', err));

    // Connect to Socket.IO
    const socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      setConnected(true);
      socket.emit('join_gallery', { event_id: slug });
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setConnected(false);
    });

    // Listen for new media uploads (real-time)
    socket.on('new_media', (doc) => {
      const item = normalizeItem(doc);
      setItems(prev => {
        if (prev.some(i => i.id === item.id)) return prev;
        setNewItemFlash(item.id);
        setTimeout(() => setNewItemFlash(null), 2500);
        return [item, ...prev];
      });
    });

    // Listen for like updates
    socket.on('media_liked', (data) => {
      setItems(prev => prev.map(i =>
        i.id === data.media_id ? { ...i, likes: data.likes, score: data.score } : i
      ));
    });

    return () => {
      socket.emit('leave_gallery', { event_id: slug });
      socket.disconnect();
    };
  }, [slug]);

  // Fetch event info
  useEffect(() => {
    fetch(`${API}/events/slug/${slug}`).then(r => r.json()).then(d => {
      setEventName(d.event_name || slug);
    }).catch(() => setEventName(slug));
  }, [slug]);

  const toggleLike = useCallback((itemId) => {
    const isLiked = likedIds.includes(itemId);
    const newLiked = isLiked ? likedIds.filter(id => id !== itemId) : [...likedIds, itemId];
    setLikedIds(newLiked);
    saveLiked(slug, newLiked);
    if (!isLiked) {
      fetch(`${API}/pocket-booth/${slug}/like/${itemId}`, { method: 'POST' }).catch(() => {});
    }
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, likes: (i.likes || 0) + (isLiked ? -1 : 1) } : i));
  }, [likedIds, slug]);

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);
  const moments = clusterIntoMoments(items);
  const highlights = [...items].sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 10);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  // Shared card renderer
  const renderCard = (item, idx, showRank) => {
    const color = COLORS[idx % COLORS.length];
    const isNew = newItemFlash === item.id;
    const isLiked = likedIds.includes(item.id);
    return (
      <div key={item.id} className={`relative rounded-2xl overflow-hidden aspect-square transition-all ${isNew ? 'ring-2 ring-green-400 scale-[1.02]' : ''}`}
        style={{ background: item.imageUrl ? '#111' : `linear-gradient(135deg, ${color}40, ${color}15)`, animation: isNew ? 'fadeInUp 0.5s ease-out' : undefined }}
        data-testid={`gallery-card-${item.id}`}>
        {showRank && (
          <div className="absolute top-2 left-2 z-10 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black"
            style={{ background: idx === 0 ? '#FFD700' : idx === 1 ? '#C0C0C0' : idx === 2 ? '#CD7F32' : 'rgba(255,255,255,0.1)', color: idx < 3 ? '#000' : '#fff' }}>
            {idx + 1}
          </div>
        )}
        <button onClick={() => setSelectedItem(item)} className="absolute inset-0 w-full h-full">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {item.type === 'booth' && <div className="text-center"><Camera size={24} weight="bold" style={{ color }} /><p className="text-[9px] mt-1 font-semibold" style={{ color }}>{item.template}</p></div>}
              {item.type === 'photo' && <Camera size={24} weight="bold" style={{ color }} />}
              {item.type === 'video' && <Play size={24} weight="fill" style={{ color }} />}
              {item.type === 'ar' && <Star size={24} weight="fill" style={{ color }} />}
            </div>
          )}
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
          <div className="flex items-center justify-between">
            <span className="text-[9px] text-white/60">{timeAgo(item.createdAt)}</span>
            <div className="flex items-center gap-2">
              {(item.likes > 0 || isLiked) && (
                <span className="text-[9px] text-[#B89762] font-semibold flex items-center gap-0.5">
                  <Heart size={10} weight={isLiked ? 'fill' : 'bold'} /> {item.likes || 0}
                </span>
              )}
              {item.type === 'booth' && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-[#B89762]/30 text-[#C29D52] font-semibold">BOOTH</span>}
              {item.type === 'video' && <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-blue-500/30 text-blue-300 font-semibold">VIDEO</span>}
            </div>
          </div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); toggleLike(item.id); }}
          className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-black/40 backdrop-blur-sm transition-all active:scale-90"
          data-testid={`like-${item.id}`}>
          <Heart size={16} weight={isLiked ? 'fill' : 'bold'} className={isLiked ? 'text-[#B89762]' : 'text-white/60'} />
        </button>
        {isNew && <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-green-500 text-[8px] font-bold text-white z-10" data-testid="new-badge">NEW</div>}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="pocket-gallery">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0F1014]/95 backdrop-blur-md border-b border-white/10">
        <div className="px-4 py-3 flex items-center justify-between">
          <BackButton fallbackHref={`/event/${slug}/pocket-booth`} label="Back" variant="subtle" testId="gallery-back" />
          <div className="text-center">
            <h1 className="font-bold text-sm">{eventName}</h1>
            <div className="flex items-center justify-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`} />
              <span className={`text-[10px] font-semibold ${connected ? 'text-green-400' : 'text-yellow-400'}`}>{connected ? 'LIVE' : 'CONNECTING'}</span>
              <span className="text-gray-500 text-[10px]">{items.length} items</span>
            </div>
          </div>
          <button onClick={() => navigate(`/event/${slug}/pocket-booth`)} className="p-2 rounded-xl bg-[#B89762]/20 text-[#B89762]" data-testid="gallery-open-booth">
            <Camera size={16} weight="bold" />
          </button>
        </div>

        {/* View tabs */}
        <div className="flex px-4 pb-2 gap-1">
          {[
            { id: 'feed', label: 'Feed', icon: <Camera size={14} weight="bold" /> },
            { id: 'timeline', label: 'Timeline', icon: <Clock size={14} weight="bold" /> },
            { id: 'highlights', label: 'Top', icon: <Trophy size={14} weight="bold" /> },
            { id: 'film', label: 'Film', icon: <FilmStrip size={14} weight="bold" /> },
          ].map(v => (
            <button key={v.id} onClick={() => v.id === 'film' ? navigate(`/event/${slug}/pocket-film`) : setView(v.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[11px] font-semibold transition-all ${view === v.id ? 'bg-[#B89762] text-white' : 'bg-white/5 text-gray-500 hover:bg-white/10'}`}
              data-testid={`view-${v.id}`}>
              {v.icon} {v.label}
            </button>
          ))}
        </div>

        {/* Type filter (feed view only) */}
        {view === 'feed' && (
          <div className="flex gap-2 px-4 pb-2 overflow-x-auto">
            {[{ id: 'all', label: 'All' }, { id: 'booth', label: 'Booth' }, { id: 'photo', label: 'Photos' }, { id: 'video', label: 'Videos' }].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-full text-[10px] font-semibold flex-shrink-0 transition-all ${filter === f.id ? 'bg-white/15 text-white' : 'bg-white/5 text-gray-500'}`}
                data-testid={`filter-${f.id}`}>
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* FEED VIEW */}
      {view === 'feed' && (
        <div className="p-3">
          {/* Live activity bar */}
          {items.length > 0 && (
            <div className="mb-3 px-1 flex items-center gap-2 text-[11px]" style={{ animation: 'fadeInDown 0.4s ease-out' }}>
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${connected ? 'bg-[#B89762]/10 border-[#B89762]/50/20 text-[#C29D52]' : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-300'}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-[#B89762]/40' : 'bg-yellow-400'} animate-pulse`} />
                {connected ? 'Live updates active' : 'Reconnecting...'}
              </div>
              <span className="text-gray-600 text-[10px]">{items.length} total</span>
            </div>
          )}

          {/* Empty state */}
          {items.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Camera size={48} weight="bold" className="text-[#B89762]/40 mb-4" />
              <p className="text-gray-400 text-sm font-semibold mb-1">No captures yet</p>
              <p className="text-gray-600 text-xs mb-6">Be the first to snap a photo!</p>
              <button onClick={() => navigate(`/event/${slug}/pocket-booth`)}
                className="px-6 py-3 rounded-2xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="empty-open-booth">
                Open Booth
              </button>
            </div>
          )}

          {/* Featured row */}
          {highlights.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 px-1 mb-2">
                <Fire size={14} weight="fill" className="text-orange-400" />
                <span className="text-xs font-bold text-white">Top Booth Moments</span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {highlights.slice(0, 5).map((item, idx) => (
                  <div key={item.id} className="flex-shrink-0 w-28 relative">
                    {renderCard(item, idx, false)}
                    {idx === 0 && <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded-full bg-orange-500/80 text-[7px] font-bold text-white">MOST LOVED</div>}
                    {idx === 1 && <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded-full bg-[#B89762]/80 text-[7px] font-bold text-white">TRENDING</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Main grid */}
          <div className="grid grid-cols-2 gap-2">
            {filtered.map((item, idx) => renderCard(item, idx, false))}
          </div>
        </div>
      )}

      {/* TIMELINE VIEW */}
      {view === 'timeline' && (
        <div className="px-4 py-4">
          {moments.length === 0 ? (
            <p className="text-gray-500 text-center py-12 text-sm">No moments yet - content will be grouped as it arrives</p>
          ) : (
            <div className="space-y-6">
              {moments.map((moment, mi) => (
                <div key={mi} data-testid={`moment-${mi}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg" style={{ background: `${moment.color}20` }}>
                      {moment.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-base" style={{ color: moment.color }}>{moment.label}</h3>
                      <p className="text-gray-500 text-[10px]">{moment.items.length} captures &bull; {timeAgo(moment.start.toISOString ? moment.start.toISOString() : moment.start)}</p>
                    </div>
                    <Fire size={16} weight="bold" className="text-gray-600" />
                  </div>
                  <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
                    {moment.items.map((item, idx) => (
                      <div key={item.id} className="flex-shrink-0 w-32">
                        {renderCard(item, idx, false)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HIGHLIGHTS VIEW */}
      {view === 'highlights' && (
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 mb-4">
            <Trophy size={20} weight="fill" className="text-yellow-400" />
            <h3 className="font-bold text-base">Top Content</h3>
            <span className="text-gray-500 text-xs ml-auto">{highlights.length} items</span>
          </div>

          {highlights.length >= 3 && (
            <div className="flex items-end gap-2 mb-6 justify-center">
              <div className="text-center w-24">
                <div className="rounded-xl overflow-hidden aspect-square mb-1 border-2 border-gray-400/30 relative">
                  {renderCard(highlights[1], 1, true)}
                </div>
                <span className="text-[10px] text-gray-400 font-semibold">{highlights[1].likes || 0} likes</span>
              </div>
              <div className="text-center w-28 -mt-4">
                <div className="rounded-xl overflow-hidden aspect-square mb-1 border-2 border-yellow-500/50 relative ring-2 ring-yellow-500/20">
                  {renderCard(highlights[0], 0, true)}
                </div>
                <span className="text-[10px] text-yellow-400 font-bold">{highlights[0].likes || 0} likes</span>
              </div>
              <div className="text-center w-24">
                <div className="rounded-xl overflow-hidden aspect-square mb-1 border-2 border-orange-700/30 relative">
                  {renderCard(highlights[2], 2, true)}
                </div>
                <span className="text-[10px] text-gray-400 font-semibold">{highlights[2].likes || 0} likes</span>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {highlights.slice(3).map((item, idx) => {
              const rank = idx + 4;
              const isLiked = likedIds.includes(item.id);
              const color = COLORS[idx % COLORS.length];
              return (
                <button key={item.id} onClick={() => setSelectedItem(item)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#1B1C22] border border-white/5 hover:border-white/10 transition-all"
                  data-testid={`highlight-${rank}`}>
                  <span className="text-sm font-black text-gray-600 w-6 text-center">{rank}</span>
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: `linear-gradient(135deg, ${color}40, ${color}15)` }}>
                    {item.imageUrl ? <img src={item.imageUrl} alt="" className="w-full h-full object-cover" /> :
                      <div className="w-full h-full flex items-center justify-center"><Camera size={16} weight="bold" style={{ color }} /></div>}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-xs font-semibold capitalize">{item.type}{item.template ? ` - ${item.template}` : ''}</p>
                    <p className="text-[10px] text-gray-500">{timeAgo(item.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] text-[#B89762] flex items-center gap-0.5"><Heart size={10} weight={isLiked ? 'fill' : 'bold'} /> {item.likes || 0}</span>
                    <span className="text-[10px] text-gray-500">{item.views || 0} views</span>
                  </div>
                </button>
              );
            })}
          </div>

          {highlights.length === 0 && (
            <p className="text-gray-500 text-center py-12 text-sm">No highlights yet - captures need likes and views to rank</p>
          )}
        </div>
      )}

      {/* Fullscreen Viewer */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="fullscreen-viewer">
          <div className="flex items-center justify-between px-4 py-4">
            <button onClick={() => setSelectedItem(null)} className="p-2 rounded-full bg-white/10 text-white" data-testid="close-viewer"><X size={20} weight="bold" /></button>
            <span className="text-gray-400 text-xs">{timeAgo(selectedItem.createdAt)}</span>
            <div className="flex gap-2">
              <button onClick={() => toggleLike(selectedItem.id)}
                className={`p-2 rounded-full transition-all ${likedIds.includes(selectedItem.id) ? 'bg-[#B89762]/20 text-[#B89762]' : 'bg-white/10 text-white'}`}
                data-testid="viewer-like">
                <Heart size={18} weight={likedIds.includes(selectedItem.id) ? 'fill' : 'bold'} />
              </button>
              <button className="p-2 rounded-full bg-white/10 text-white" data-testid="viewer-share"><ShareNetwork size={18} weight="bold" /></button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center p-4">
            {selectedItem.imageUrl ? (
              <img src={selectedItem.imageUrl} alt="" className="max-h-full max-w-full rounded-xl" />
            ) : (
              <div className="w-64 h-80 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${COLORS[0]}40, ${COLORS[2]}15)` }}>
                <Camera size={48} weight="bold" className="text-[#B89762]" />
              </div>
            )}
          </div>
          <div className="px-6 pb-2 flex items-center justify-center gap-6">
            <span className="text-sm text-[#B89762] flex items-center gap-1"><Heart size={16} weight="fill" /> {selectedItem.likes || 0} likes</span>
            <span className="text-sm text-gray-400 flex items-center gap-1"><Play size={16} weight="bold" /> {selectedItem.views || 0} views</span>
          </div>
          <div className="px-6 pb-8 flex gap-3">
            <button className="flex-1 h-12 rounded-xl bg-white/10 text-white font-semibold text-sm flex items-center justify-center gap-2" data-testid="viewer-save">
              <DownloadSimple size={16} weight="bold" /> Save
            </button>
            <button className="flex-1 h-12 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }} data-testid="viewer-share-btn">
              <ShareNetwork size={16} weight="bold" /> Share
            </button>
          </div>
        </div>
      )}

      {/* Floating booth button */}
      <button onClick={() => navigate(`/event/${slug}/pocket-booth`)}
        className="fixed bottom-6 right-6 w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shadow-[#B89762]/30 z-30 transition-all active:scale-90"
        style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        data-testid="gallery-fab">
        <Camera size={24} weight="bold" className="text-white" />
      </button>

      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default PocketGallery;
